/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import ConfirmModal from '../../components/ConfirmModal';
import adminApi from '../../services/adminApi';

const SOURCES = [
  {
    id: 'manual',
    label: '📋 Manual (paste JSON)',
    description: 'Paste JSON from Perplexity Comet or any source'
  },
  {
    id: 'claude',
    label: '🤖 Claude (web search)',
    description: 'Claude searches the web automatically'
  },
  {
    id: 'perplexity',
    label: '🔍 Perplexity API',
    description: 'Requires PERPLEXITY_API_KEY'
  }
];

const DOMAINS = ['wms','iseries','civil','stitching'];
const ENTITY_TYPES = ['all','individual','university','organization'];

const ResearchStudio = () => {
  const [activeTab, setActiveTab] = useState('import');
  const [source, setSource] = useState('manual');
  const [domain, setDomain] = useState('wms');
  const [entityType, setEntityType] = useState('all');
  const [query, setQuery] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [results, setResults] = useState(null);
  const [staging, setStaging] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');

  // Phase 1 UX additions: import confirm + bulk reject
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImport, setPendingImport] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [pendingBulk, setPendingBulk] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const abortControllerRef = useRef(null);

  const allSelected = staging.length > 0 && selectedIds.size === staging.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < staging.length;

  useEffect(() => {
    fetchStaging();
  }, [activeTab, domain]);

  useEffect(() => {
    // Pre-fetch staging count for badge
    adminApi.get('/api/admin/wiki/research/staging')
      .then(res => {
        const rawData = res?.data ?? res;
        const entries = rawData?.entries
          || rawData?.data?.entries
          || (Array.isArray(rawData) ? rawData : []);
        setStaging(entries);
      })
      .catch(() => {});
  }, []);

  const fetchStaging = async () => {
    if (activeTab !== 'staging') return;
    try {
      const url = domain && domain !== 'all'
        ? `/api/admin/wiki/research/staging?domain=${domain}`
        : '/api/admin/wiki/research/staging';
      const res = await adminApi.get(url);
      console.log('[ResearchStudio] Staging response:',
        res.data?.count, 'entries:', res.data?.entries?.length);
      const rawData = res?.data ?? res;
      console.log('[ResearchStudio] Full response:',
        JSON.stringify(rawData || 'empty')
          .substring(0, 300));
      const entries = rawData?.entries
        || rawData?.data?.entries
        || (Array.isArray(rawData) ? rawData : []);
      setStaging(entries);
      // Prune any selectedIds that no longer exist in the new list
      setSelectedIds(prev => {
        if (prev.size === 0) return prev;
        const valid = new Set(entries.map(e => e.id));
        const next = new Set();
        prev.forEach(id => { if (valid.has(id)) next.add(id); });
        return next.size === prev.size ? prev : next;
      });
    } catch(e) {
      console.error('[ResearchStudio] fetchStaging error:', e);
    }
  };

  // Stage 1 — gather resources (parse JSON or run Claude search), then
  // open the import confirmation modal. The actual /import POST is sent
  // by executeImport() only after the user confirms.
  const handleResearch = async () => {
    setError('');
    setResults(null);

    if (source === 'manual') {
      let parsed;
      try {
        parsed = JSON.parse(jsonInput);
      } catch(e) {
        setError('Invalid JSON — check your paste');
        return;
      }

      let resources = [];
      if (Array.isArray(parsed)) {
        resources = parsed;
      } else if (parsed.categories) {
        resources = parsed.categories.flatMap(c => c.resources || []);
      } else if (parsed.resources) {
        resources = parsed.resources;
      }

      if (resources.length === 0) {
        setError('No resources found in JSON');
        return;
      }

      setPendingImport({ source: 'manual', resources, count: resources.length });
      setShowImportConfirm(true);
      return;
    }

    if (source === 'claude') {
      if (!query.trim()) {
        setError('Enter a research query');
        return;
      }

      setLoading(true);
      try {
        const searchRes = await adminApi.post('/api/admin/wiki/research/claude', {
          query, domain, entity_type: entityType
        });
        const searchBody = searchRes?.data ?? searchRes;
        const searchInner = searchBody?.data ?? searchBody;
        const resources = searchInner?.resources;

        if (!resources?.length) {
          setError('No results found');
          return;
        }

        setPendingImport({ source: 'claude', resources, count: resources.length });
        setShowImportConfirm(true);
      } catch(e) {
        setError(e.response?.data?.error || e.message || 'Search failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (source === 'perplexity') {
      setError('Perplexity API key not configured. Add PERPLEXITY_API_KEY to Render env vars.');
    }
  };

  // Stage 2 — actually send the /import POST after confirmation
  const executeImport = async () => {
    if (!pendingImport) return;
    setShowImportConfirm(false);
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.post('/api/admin/wiki/research/import', {
        resources: pendingImport.resources,
        domain,
        source: pendingImport.source,
      });
      const body = res?.data ?? res;
      setResults(body?.data ?? body);
    } catch(e) {
      setError(e.response?.data?.error || e.message || 'Import failed');
    } finally {
      setLoading(false);
      setPendingImport(null);
    }
  };

  const cancelImport = () => {
    setShowImportConfirm(false);
    setPendingImport(null);
  };

  const handleApprove = async (id) => {
    setActionLoading(p => ({...p, [id]: 'approve'}));
    try {
      await adminApi.post(`/api/admin/wiki/research/approve/${id}`);
      fetchStaging();
    } catch(e) {
      console.error(e);
    } finally {
      setActionLoading(p => {
        const n = {...p}; delete n[id]; return n;
      });
    }
  };

  const handleReject = async (id) => {
    setActionLoading(p => ({...p, [id]: 'reject'}));
    try {
      await adminApi.post(`/api/admin/wiki/research/reject/${id}`);
      fetchStaging();
    } catch(e) {
      console.error(e);
    } finally {
      setActionLoading(p => {
        const n = {...p}; delete n[id]; return n;
      });
    }
  };

  // ─── Bulk reject ──────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(staging.map(e => e.id)));
    }
  };

  const openBulkRejectConfirm = (mode) => {
    const ids = mode === 'selected'
      ? Array.from(selectedIds)
      : staging.map(e => e.id);
    if (!ids.length) return;
    setPendingBulk({ mode, ids });
    setShowBulkConfirm(true);
  };

  const cancelBulkConfirm = () => {
    setShowBulkConfirm(false);
    setPendingBulk(null);
  };

  const confirmBulkReject = () => {
    if (!pendingBulk) return;
    const ids = pendingBulk.ids;
    setShowBulkConfirm(false);
    setPendingBulk(null);
    handleBulkReject(ids);
  };

  // Sequential reject with cancellation. Continues past per-item failures
  // and reports a summary at the end. Refreshes staging once on completion.
  const handleBulkReject = async (idsToReject) => {
    if (!idsToReject.length) return;
    abortControllerRef.current = new AbortController();
    const total = idsToReject.length;
    setBulkProgress({ current: 0, total, cancelling: false });

    let succeeded = 0;
    let failed = 0;
    const errors = [];

    for (let i = 0; i < idsToReject.length; i++) {
      if (abortControllerRef.current.signal.aborted) break;
      const id = idsToReject[i];
      setBulkProgress(p => p ? { ...p, current: i + 1 } : p);
      try {
        await adminApi.post(`/api/admin/wiki/research/reject/${id}`);
        succeeded++;
      } catch (e) {
        failed++;
        const reason = e.response?.data?.error || e.message || 'unknown';
        errors.push({ id, error: reason });
        console.error('[BulkReject] Failed', id, e);
      }
    }

    const wasCancelled = abortControllerRef.current?.signal.aborted;
    abortControllerRef.current = null;

    let summary;
    if (wasCancelled) {
      summary = `Cancelled. Rejected ${succeeded} of ${total}.` +
        (failed ? ` ${failed} failed (see console).` : '');
    } else if (failed === 0) {
      summary = `Successfully rejected ${succeeded} ${succeeded === 1 ? 'entry' : 'entries'}.`;
    } else {
      summary = `Completed: ${succeeded} succeeded, ${failed} failed. See console for details.`;
    }

    // eslint-disable-next-line no-alert
    window.alert(summary);

    setBulkProgress(null);
    setSelectedIds(new Set());
    fetchStaging();
  };

  const cancelBulkReject = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setBulkProgress(p => p ? { ...p, cancelling: true } : p);
    }
  };

  const getRecommendationColor = (rec) => ({
    'approve': '#16a34a',
    'review': '#ca8a04',
    'reject': '#dc2626'
  }[rec] || '#94a3b8');

  const getEntryTypeIcon = (type) => ({
    'video': '📺',
    'course': '🎓',
    'job': '💼',
    'community': '👥',
    'article': '📰',
    'discovery': '🔍'
  }[type] || '📄');

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div style={{padding:'24px', maxWidth:'1100px', margin:'0 auto'}}>

        {/* Header */}
        <div style={{
          display:'flex',
          justifyContent:'space-between',
          alignItems:'center',
          marginBottom:'24px'
        }}>
          <div>
            <h1 style={{
              fontSize:'20px', fontWeight:'700',
              color:'#1e293b', margin:'0 0 4px'
            }}>
              🔬 Research Studio
            </h1>
            <p style={{
              color:'#64748b', margin:0, fontSize:'14px'
            }}>
              Import, audit and curate domain knowledge for SkillDelivery
            </p>
          </div>
          {staging.length > 0 && (
            <span style={{
              background:'#fef3c7',
              color:'#92400e',
              padding:'6px 14px',
              borderRadius:'20px',
              fontSize:'13px',
              fontWeight:'600'
            }}>
              {staging.length} awaiting review
            </span>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          display:'flex', gap:'8px',
          marginBottom:'24px',
          borderBottom:'2px solid #e2e8f0'
        }}>
          {[
            { key:'import', label:'📥 Import' },
            { key:'staging', label:`⏳ Staging ${staging.length > 0 ? `(${staging.length})` : ''}` },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding:'10px 20px',
                border:'none', background:'none',
                cursor:'pointer', fontSize:'14px',
                fontWeight: activeTab===tab.key ? '600' : '400',
                color: activeTab===tab.key ? '#7c3aed' : '#64748b',
                borderBottom: activeTab===tab.key
                  ? '2px solid #7c3aed'
                  : '2px solid transparent',
                marginBottom:'-2px'
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* IMPORT TAB */}
        {activeTab === 'import' && (
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'24px'
          }}>
            {/* Left — Source Config */}
            <div style={{
              background:'white',
              border:'1px solid #e2e8f0',
              borderRadius:'12px',
              padding:'20px'
            }}>
              <h3 style={{
                margin:'0 0 16px', fontSize:'15px', color:'#1e293b'
              }}>
                Research Source
              </h3>

              {/* Source selector */}
              <div style={{marginBottom:'16px'}}>
                {SOURCES.map(s => (
                  <div key={s.id}
                    onClick={() => setSource(s.id)}
                    style={{
                      padding:'12px',
                      borderRadius:'8px',
                      border: source===s.id
                        ? '2px solid #7c3aed'
                        : '1px solid #e2e8f0',
                      marginBottom:'8px',
                      cursor:'pointer',
                      background: source===s.id ? '#faf5ff' : 'white'
                    }}>
                    <div style={{
                      fontWeight:'600', fontSize:'14px',
                      color: source===s.id ? '#7c3aed' : '#1e293b'
                    }}>
                      {s.label}
                    </div>
                    <div style={{
                      fontSize:'12px', color:'#94a3b8', marginTop:'2px'
                    }}>
                      {s.description}
                    </div>
                  </div>
                ))}
              </div>

              {/* Domain + Entity */}
              <div style={{
                display:'grid',
                gridTemplateColumns:'1fr 1fr',
                gap:'12px',
                marginBottom:'16px'
              }}>
                <div>
                  <label style={{
                    fontSize:'13px', color:'#64748b',
                    display:'block', marginBottom:'4px'
                  }}>Domain</label>
                  <select
                    value={domain}
                    onChange={e => setDomain(e.target.value)}
                    style={{
                      width:'100%', padding:'8px',
                      borderRadius:'8px',
                      border:'1px solid #e2e8f0',
                      fontSize:'14px'
                    }}>
                    {DOMAINS.map(d => (
                      <option key={d} value={d}>{d.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{
                    fontSize:'13px', color:'#64748b',
                    display:'block', marginBottom:'4px'
                  }}>Entity</label>
                  <select
                    value={entityType}
                    onChange={e => setEntityType(e.target.value)}
                    style={{
                      width:'100%', padding:'8px',
                      borderRadius:'8px',
                      border:'1px solid #e2e8f0',
                      fontSize:'14px'
                    }}>
                    {ENTITY_TYPES.map(e => (
                      <option key={e} value={e}>
                        {e.charAt(0).toUpperCase() + e.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Claude query input */}
              {source === 'claude' && (
                <div style={{marginBottom:'16px'}}>
                  <label style={{
                    fontSize:'13px', color:'#64748b',
                    display:'block', marginBottom:'4px'
                  }}>
                    Research Query
                  </label>
                  <textarea
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="e.g. best WMS YouTube channels for beginners 2026 India"
                    rows={3}
                    style={{
                      width:'100%', padding:'10px',
                      borderRadius:'8px',
                      border:'1px solid #e2e8f0',
                      fontSize:'14px',
                      resize:'vertical',
                      boxSizing:'border-box'
                    }}
                  />
                </div>
              )}

              {/* Manual JSON paste */}
              {source === 'manual' && (
                <div style={{marginBottom:'16px'}}>
                  <label style={{
                    fontSize:'13px', color:'#64748b',
                    display:'block', marginBottom:'4px'
                  }}>
                    Paste JSON from Perplexity Comet or any source
                  </label>
                  <textarea
                    value={jsonInput}
                    onChange={e => setJsonInput(e.target.value)}
                    placeholder={'{\n  "categories": [...]\n}\nor\n[\n  {"name": "...", "url": "..."}\n]'}
                    rows={8}
                    style={{
                      width:'100%', padding:'10px',
                      borderRadius:'8px',
                      border:'1px solid #e2e8f0',
                      fontSize:'12px',
                      fontFamily:'monospace',
                      resize:'vertical',
                      boxSizing:'border-box'
                    }}
                  />
                </div>
              )}

              {/* Perplexity placeholder */}
              {source === 'perplexity' && (
                <div style={{
                  padding:'16px',
                  background:'#fef3c7',
                  borderRadius:'8px',
                  fontSize:'14px',
                  color:'#92400e',
                  marginBottom:'16px'
                }}>
                  Add PERPLEXITY_API_KEY to Render environment variables to enable this source.
                </div>
              )}

              {error && (
                <div style={{
                  padding:'10px',
                  background:'#fef2f2',
                  borderRadius:'8px',
                  color:'#dc2626',
                  fontSize:'13px',
                  marginBottom:'12px'
                }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleResearch}
                disabled={loading || source === 'perplexity'}
                style={{
                  width:'100%', padding:'12px',
                  background: loading ? '#94a3b8' : '#7c3aed',
                  color:'white', border:'none',
                  borderRadius:'8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize:'14px', fontWeight:'600'
                }}>
                {loading
                  ? '⏳ Processing...'
                  : source === 'manual'
                    ? '📥 Import & Audit JSON'
                    : '🔍 Research & Import'}
              </button>
            </div>

            {/* Right — Results */}
            <div>
              {!results && !loading && (
                <div style={{
                  background:'white',
                  border:'1px solid #e2e8f0',
                  borderRadius:'12px',
                  padding:'40px',
                  textAlign:'center',
                  color:'#94a3b8'
                }}>
                  <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔬</div>
                  <p>Select a source and run research to see results</p>
                  <p style={{fontSize:'13px'}}>Results will be AI-audited before staging</p>
                </div>
              )}

              {loading && (
                <div style={{
                  background:'white',
                  border:'1px solid #e2e8f0',
                  borderRadius:'12px',
                  padding:'40px',
                  textAlign:'center'
                }}>
                  <div style={{ fontSize:'32px', marginBottom:'16px' }}>⏳</div>
                  <p style={{color:'#64748b'}}>
                    {source === 'claude'
                      ? 'Claude is searching the web...'
                      : 'Importing and auditing resources...'}
                  </p>
                </div>
              )}

              {results && (
                <div>
                  {/* Summary */}
                  <div style={{
                    background:'white',
                    border:'1px solid #e2e8f0',
                    borderRadius:'12px',
                    padding:'16px',
                    marginBottom:'16px',
                    display:'grid',
                    gridTemplateColumns:'repeat(3,1fr)',
                    gap:'12px',
                    textAlign:'center'
                  }}>
                    {[
                      { label:'Staged', value: results.staged, color:'#16a34a' },
                      { label:'Recommended', value: results.recommended_for_approval, color:'#7c3aed' },
                      { label:'Failed', value: results.failed, color:'#dc2626' }
                    ].map((s,i) => (
                      <div key={i}>
                        <div style={{ fontSize:'24px', fontWeight:'700', color:s.color }}>
                          {s.value}
                        </div>
                        <div style={{ fontSize:'12px', color:'#64748b' }}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Go to staging */}
                  <button
                    onClick={() => setActiveTab('staging')}
                    style={{
                      width:'100%', padding:'12px',
                      background:'#16a34a',
                      color:'white', border:'none',
                      borderRadius:'8px',
                      cursor:'pointer',
                      fontSize:'14px', fontWeight:'600',
                      marginBottom:'12px'
                    }}>
                    → Review {results.staged} staged resources
                  </button>

                  {/* Individual results */}
                  <div style={{ maxHeight:'400px', overflowY:'auto' }}>
                    {(results.results || []).map((r, i) => (
                      <div key={i} style={{
                        background:'white',
                        border:'1px solid #e2e8f0',
                        borderLeft: `3px solid ${getRecommendationColor(r.audit_recommendation)}`,
                        borderRadius:'8px',
                        padding:'12px',
                        marginBottom:'8px'
                      }}>
                        <div style={{
                          display:'flex',
                          justifyContent:'space-between',
                          alignItems:'flex-start'
                        }}>
                          <span style={{
                            fontSize:'14px', fontWeight:'500',
                            color:'#1e293b', flex:1
                          }}>
                            {getEntryTypeIcon(r.entry_type)} {r.name}
                          </span>
                          <span style={{
                            fontSize:'11px',
                            padding:'2px 8px',
                            borderRadius:'12px',
                            background: getRecommendationColor(r.audit_recommendation) + '20',
                            color: getRecommendationColor(r.audit_recommendation),
                            marginLeft:'8px',
                            flexShrink:0
                          }}>
                            {r.audit_recommendation || 'review'}
                          </span>
                        </div>
                        {r.audit_reason && (
                          <div style={{
                            fontSize:'12px', color:'#94a3b8', marginTop:'4px'
                          }}>
                            {r.audit_reason}
                          </div>
                        )}
                        {r.error && (
                          <div style={{
                            fontSize:'12px', color:'#dc2626', marginTop:'4px'
                          }}>
                            Error: {r.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STAGING TAB */}
        {activeTab === 'staging' && (
          <div>
            <div style={{
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
              marginBottom:'16px'
            }}>
              <span style={{ color:'#64748b', fontSize:'14px' }}>
                {staging.length} resources awaiting your review
              </span>
              <select
                value={domain}
                onChange={e => setDomain(e.target.value)}
                style={{
                  padding:'8px 12px',
                  borderRadius:'8px',
                  border:'1px solid #e2e8f0',
                  fontSize:'14px'
                }}>
                <option value="all">All Domains</option>
                {DOMAINS.map(d => (
                  <option key={d} value={d}>{d.toUpperCase()}</option>
                ))}
              </select>
            </div>

            {staging.length === 0 ? (
              <div style={{
                background:'white',
                border:'1px solid #e2e8f0',
                borderRadius:'12px',
                padding:'60px',
                textAlign:'center'
              }}>
                <div style={{ fontSize:'48px', marginBottom:'16px' }}>✅</div>
                <h3 style={{color:'#1e293b'}}>Nothing to review</h3>
                <p style={{color:'#94a3b8'}}>Import resources to see them here for review</p>
              </div>
            ) : (
              <>
                {/* Bulk action toolbar */}
                <div style={{
                  background:'#f8fafc',
                  border:'1px solid #e2e8f0',
                  borderRadius:'8px',
                  padding:'12px 16px',
                  marginBottom:'12px',
                  display:'flex',
                  alignItems:'center',
                  gap:'12px',
                  flexWrap:'wrap'
                }}>
                  <input
                    type="checkbox"
                    aria-label="Select all in view"
                    ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={!!bulkProgress}
                    style={{ cursor: bulkProgress ? 'not-allowed' : 'pointer' }}
                  />
                  <span style={{ fontSize:'13px', color:'#64748b' }}>
                    {selectedIds.size > 0
                      ? `${selectedIds.size} selected`
                      : `Select all in view (${staging.length})`}
                  </span>
                  <div style={{ marginLeft:'auto', display:'flex', gap:'8px' }}>
                    {selectedIds.size > 0 && (
                      <button
                        onClick={() => openBulkRejectConfirm('selected')}
                        disabled={!!bulkProgress}
                        style={{
                          padding:'8px 14px',
                          background:'white',
                          color:'#dc2626',
                          border:'1px solid #dc2626',
                          borderRadius:'8px',
                          cursor: bulkProgress ? 'not-allowed' : 'pointer',
                          fontSize:'13px',
                          fontWeight:'500'
                        }}>
                        ❌ Reject Selected ({selectedIds.size})
                      </button>
                    )}
                    <button
                      onClick={() => openBulkRejectConfirm('all')}
                      disabled={!!bulkProgress || staging.length === 0}
                      style={{
                        padding:'8px 14px',
                        background:'#fef2f2',
                        color:'#991b1b',
                        border:'1px solid #fecaca',
                        borderRadius:'8px',
                        cursor: bulkProgress ? 'not-allowed' : 'pointer',
                        fontSize:'13px',
                        fontWeight:'500'
                      }}>
                      ❌ Reject All in View ({staging.length})
                    </button>
                  </div>
                </div>
                {staging.map(entry => {
                const audit = entry.source_metadata?.audit || {};
                const recColor = getRecommendationColor(audit.recommendation);

                return (
                  <div key={entry.id} style={{
                    background:'white',
                    border:'1px solid #e2e8f0',
                    borderLeft:`4px solid ${recColor}`,
                    borderRadius:'12px',
                    padding:'20px',
                    marginBottom:'12px',
                    display:'flex',
                    gap:'12px'
                  }}>
                    <div style={{ paddingTop:'2px' }}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${entry.topic || entry.id}`}
                        checked={selectedIds.has(entry.id)}
                        onChange={() => toggleSelect(entry.id)}
                        disabled={!!bulkProgress || !!actionLoading[entry.id]}
                        style={{
                          cursor: (bulkProgress || actionLoading[entry.id])
                            ? 'not-allowed' : 'pointer'
                        }}
                      />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                    <div style={{
                      display:'flex',
                      justifyContent:'space-between',
                      alignItems:'flex-start',
                      marginBottom:'12px'
                    }}>
                      <div>
                        <div style={{
                          display:'flex', alignItems:'center',
                          gap:'8px', marginBottom:'4px'
                        }}>
                          <span style={{ fontSize:'16px' }}>
                            {getEntryTypeIcon(entry.entry_type)}
                          </span>
                          <h3 style={{
                            margin:0, fontSize:'15px',
                            fontWeight:'600', color:'#1e293b'
                          }}>
                            {entry.topic}
                          </h3>
                        </div>
                        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                          <span style={{
                            fontSize:'12px', background:'#f1f5f9',
                            color:'#64748b', padding:'2px 8px',
                            borderRadius:'12px'
                          }}>
                            {entry.domain?.toUpperCase()}
                          </span>
                          <span style={{
                            fontSize:'12px', background:'#f1f5f9',
                            color:'#64748b', padding:'2px 8px',
                            borderRadius:'12px'
                          }}>
                            {entry.entry_type}
                          </span>
                          <span style={{
                            fontSize:'12px',
                            background: recColor + '15',
                            color: recColor,
                            padding:'2px 8px',
                            borderRadius:'12px',
                            fontWeight:'500'
                          }}>
                            AI: {audit.recommendation || 'review'}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize:'12px', color:'#94a3b8' }}>
                        Quality: {Math.round((entry.quality_score||0)*100)}%
                      </div>
                    </div>

                    <p style={{
                      color:'#475569', fontSize:'14px',
                      lineHeight:'1.6', marginBottom:'12px'
                    }}>
                      {(entry.summary||'').substring(0,250)}
                    </p>

                    {entry.source_metadata?.source_url && (
                      <a
                        href={entry.source_metadata.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize:'13px', color:'#7c3aed',
                          display:'block', marginBottom:'12px',
                          wordBreak:'break-all'
                        }}>
                        🔗 {entry.source_metadata.source_url}
                      </a>
                    )}

                    {audit.reason && (
                      <div style={{
                        padding:'8px 12px',
                        background:'#f8fafc',
                        borderRadius:'8px',
                        fontSize:'13px',
                        color:'#64748b',
                        marginBottom:'12px'
                      }}>
                        🤖 {audit.reason}
                      </div>
                    )}

                    <div style={{ display:'flex', gap:'8px' }}>
                      <button
                        onClick={() => handleApprove(entry.id)}
                        disabled={!!actionLoading[entry.id] || !!bulkProgress}
                        style={{
                          padding:'8px 16px',
                          background:'#16a34a',
                          color:'white', border:'none',
                          borderRadius:'8px',
                          cursor: (actionLoading[entry.id] || bulkProgress)
                            ? 'not-allowed' : 'pointer',
                          fontSize:'13px', fontWeight:'500',
                          opacity: bulkProgress ? 0.5 : 1
                        }}>
                        {actionLoading[entry.id] === 'approve' ? '...' : '✅ Approve & Publish'}
                      </button>
                      <button
                        onClick={() => handleReject(entry.id)}
                        disabled={!!actionLoading[entry.id] || !!bulkProgress}
                        style={{
                          padding:'8px 16px',
                          background:'white',
                          color:'#dc2626',
                          border:'1px solid #dc2626',
                          borderRadius:'8px',
                          cursor: (actionLoading[entry.id] || bulkProgress)
                            ? 'not-allowed' : 'pointer',
                          fontSize:'13px', fontWeight:'500',
                          opacity: bulkProgress ? 0.5 : 1
                        }}>
                        {actionLoading[entry.id] === 'reject' ? '...' : '❌ Reject'}
                      </button>
                    </div>
                    </div>
                  </div>
                );
              })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Import confirmation modal */}
      <ConfirmModal
        show={showImportConfirm}
        onCancel={cancelImport}
        onConfirm={executeImport}
        title="Confirm Import"
        confirmLabel="Confirm Import"
        confirmVariant="primary"
        message={pendingImport ? (
          <div>
            <p>
              About to import{' '}
              <strong>{pendingImport.count} resource{pendingImport.count === 1 ? '' : 's'}</strong>
              {' '}as{' '}
              <strong>{domain.toUpperCase()}</strong>
              {' '}({entityType}).
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Source: {pendingImport.source === 'manual' ? 'Manual JSON' : 'Claude search'}
            </p>
          </div>
        ) : null}
      />

      {/* Bulk reject confirmation modal */}
      <ConfirmModal
        show={showBulkConfirm}
        onCancel={cancelBulkConfirm}
        onConfirm={confirmBulkReject}
        title="Confirm Bulk Reject"
        confirmLabel="Yes, Reject"
        confirmVariant="danger"
        message={pendingBulk ? (
          pendingBulk.mode === 'selected'
            ? `Reject ${pendingBulk.ids.length} selected ${pendingBulk.ids.length === 1 ? 'entry' : 'entries'}? This cannot be undone.`
            : `Reject all ${pendingBulk.ids.length} ${pendingBulk.ids.length === 1 ? 'entry' : 'entries'} currently shown? This cannot be undone.`
        ) : null}
      />

      {/* Bulk reject progress overlay */}
      {bulkProgress && (
        <div style={{
          position:'fixed',
          top:0, left:0, right:0, bottom:0,
          background:'rgba(0,0,0,0.5)',
          display:'flex',
          alignItems:'center',
          justifyContent:'center',
          zIndex:1000
        }}>
          <div style={{
            background:'white',
            borderRadius:'12px',
            padding:'32px',
            minWidth:'320px',
            textAlign:'center',
            boxShadow:'0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontSize:'24px', marginBottom:'12px' }}>
              {bulkProgress.cancelling ? '⏸️ Cancelling…' : '⏳ Rejecting…'}
            </div>
            <div style={{ fontSize:'14px', color:'#64748b', marginBottom:'16px' }}>
              {bulkProgress.cancelling
                ? `Stopping after current request… ${bulkProgress.current} of ${bulkProgress.total}`
                : `Rejecting ${bulkProgress.current} of ${bulkProgress.total}…`}
            </div>
            <div style={{
              width:'100%',
              height:'8px',
              background:'#e2e8f0',
              borderRadius:'4px',
              overflow:'hidden',
              marginBottom:'16px'
            }}>
              <div style={{
                width: `${bulkProgress.total > 0
                  ? Math.round((bulkProgress.current / bulkProgress.total) * 100)
                  : 0}%`,
                height:'100%',
                background:'#7c3aed',
                transition:'width 0.2s'
              }} />
            </div>
            <button
              onClick={cancelBulkReject}
              disabled={bulkProgress.cancelling}
              style={{
                padding:'8px 16px',
                background:'white',
                color:'#dc2626',
                border:'1px solid #dc2626',
                borderRadius:'8px',
                cursor: bulkProgress.cancelling ? 'not-allowed' : 'pointer',
                fontSize:'13px',
                fontWeight:'500',
                opacity: bulkProgress.cancelling ? 0.6 : 1
              }}>
              {bulkProgress.cancelling ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ResearchStudio;
